import { memo, useEffect, useState } from "react";
import { useParams } from "react-router";

import { skipToken } from "@reduxjs/toolkit/query/react";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { groupOrderAttributesByAddressPlacement, useFeatureFlags } from "@jti/commerce";
import { Box } from "@jti/core-components/components/Box";
import { Loader } from "@jti/core-components/components/Loader";
import { customPaymentMethods } from "@jti/fe-common/types";
import type { IOrderAttributeOutput } from "@jti/magento";
import { useGetOrderAttributesQuery } from "@jti/store/slices/magentoApiSlice";
import { useGetOrderPaymentMethodQuery } from "@jti/store/slices/paymentApiSlice";
import { formatDate, Typography, useTranslations } from "@jti/ui";

import { OrderItemsSummaryContainer } from "@checkout-v2/features/ItemsSummary/OrderItemsSummaryContainer/OrderItemsSummaryContainer";
import { OrderPaymentSummaryContainer } from "@checkout-v2/features/PaymentSummary";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import { getNormalizedRegion } from "../../utils/getNormalizedRegion";

import { OrderAddresses } from "./components/OrderAddresses";
import { OrderPaymentMethod } from "./components/OrderPaymentMethod";

import styles from "./OrderSection.module.scss";

const { sanitize } = dompurify;

export const OrderSection = memo(() => {
  const { orderNumber: orderId, paymentToken } = useParams<{ orderNumber?: string; paymentToken?: string }>();
  const { data: order, isLoading, isSuccess } = useCustomerOrder(orderId);
  const { data: paymentMethod } = useGetOrderPaymentMethodQuery(paymentToken ?? skipToken);
  const orderFetchStatus = isLoading ? "pending" : isSuccess ? "success" : "error";

  const checkoutConfig = useCheckoutV2Configuration();

  const zeroValueOrderNotification = checkoutConfig.general?.zeroValueOrderNotificationText ?? "";
  const { COUNTRY_NAME, dateFormat } = useConfig();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();
  const locale = useLocale();
  const { __ } = useTranslations();

  const {
    displayBillingAddress,
    displayDeliveryMethod,
    displayItemsSummary,
    displayPaymentMethod,
    displayShippingAddress,
  } = checkoutConfig.orderSummaryConfig;

  const { data: orderAttributes = [] } = useGetOrderAttributesQuery();
  const [shippingAttributes, setShippingAttributes] = useState<IOrderAttributeOutput[]>([]);
  const [billingAttributes, setBillingAttributes] = useState<IOrderAttributeOutput[]>([]);

  useEffect(() => {
    if (order?.additional_attributes && orderAttributes.length) {
      const { updatedShippingAttributes, updatedBillingAttributes } = groupOrderAttributesByAddressPlacement(
        order.additional_attributes,
        orderAttributes
      );

      if (updatedShippingAttributes) {
        setShippingAttributes(updatedShippingAttributes);
      }
      if (updatedBillingAttributes) {
        setBillingAttributes(updatedBillingAttributes);
      }
    }
  }, [order?.additional_attributes, orderAttributes.length, orderAttributes]);

  const shouldDisplayBoxSection =
    displayBillingAddress || displayDeliveryMethod || displayPaymentMethod || displayShippingAddress;

  const isPayOnDelivery = order?.paymentMethod === customPaymentMethods.payOnDelivery;

  if (orderFetchStatus === "pending") return <Loader />;

  return (
    <>
      <div className={styles.numberDateContainer} data-testid="order-basic-info-section">
        <Box data-testid="order-info-box-number" title={__("cart.order.number")} titleAs="h3" variant="level-2">
          <Typography variant="body1" dataTestId="order-info-box-value">
            {__("cart.order.summary.numberFormat", [order?.number || "000000000"])}
          </Typography>
        </Box>
        <Box data-testid="order-info-box-date" title={__("cart.order.date")} titleAs="h3" variant="level-2">
          <Typography variant="body1" dataTestId="order-info-box-value">
            {formatDate(order?.orderDate, dateFormat || "ddd D MMMM YYYY", locale, true) || "..."}
          </Typography>
        </Box>
      </div>

      {displayItemsSummary && !ffCheckoutV2EnableIteration2 && <OrderItemsSummaryContainer />}

      <OrderPaymentSummaryContainer />

      {shouldDisplayBoxSection && (
        <div className={styles.boxes}>
          {displayShippingAddress && order?.shippingAddress && (
            <OrderAddresses
              address={{
                ...order.shippingAddress,
                region: getNormalizedRegion(order.shippingAddress.region),
                countryName: COUNTRY_NAME,
              }}
              className={styles.shipping}
              title={__("cart.order.shipping_address.title")}
              orderAttributesInfos={shippingAttributes}
            />
          )}

          {displayDeliveryMethod && (
            <div className={styles.delivery}>
              <Box title={__("cart.order.delivery_method.title")} variant="level-2">
                {order?.shippingMethod?.methodCode ?? "..."}
              </Box>
            </div>
          )}

          {displayBillingAddress && order?.billingAddress && (
            <OrderAddresses
              address={{
                ...order.billingAddress,
                region: getNormalizedRegion(order.billingAddress.region),
                countryName: COUNTRY_NAME,
              }}
              className={styles.billing}
              title={__("cart.order.billing_address.title")}
              orderAttributesInfos={billingAttributes}
            />
          )}

          {displayPaymentMethod && (
            <div className={styles.payment}>
              <Box title={__("cart.order.payment_method.title")} variant="level-2">
                {order?.isZeroValueOrder ? (
                  <div
                    data-testid="zero-value-order-message"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(zeroValueOrderNotification),
                    }}
                  ></div>
                ) : (
                  <OrderPaymentMethod isPayOnDelivery={isPayOnDelivery} paymentMethod={paymentMethod} />
                )}
              </Box>
            </div>
          )}
        </div>
      )}
    </>
  );
});
